import React, { useState, useEffect } from 'react';
import { userAPI, appointmentAPI } from '../../services/api';
import { formatDateForInput } from '../../utils/dateTimeHelpers';
import '../../styles/AdminStyles.css';

function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalAppointments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all users (using high limit to get accurate count)
      const users = await userAPI.getAllUsers(10000, 0);
      const now = new Date();
      const activeUsers = users.filter((user) => {
        const endDate = new Date(user.end_date);
        return endDate >= now;
      });

      // Local calendar "today" (not UTC — toISOString() breaks IL evenings vs DB dates)
      const today = formatDateForInput(new Date());

      // Same-day range so API returns only today's rows (avoids LIMIT 100 + ORDER BY date DESC
      // excluding today when many future bookings exist)
      const appointments = await appointmentAPI.getAllAppointments(today, 500, 0, today);

      const todaysAppointments = appointments.filter((apt) => apt.training_date === today);

      setStats({
        totalUsers: users.length,
        activeUsers: activeUsers.length,
        totalAppointments: appointments.length,
      });

      setTodayAppointments(todaysAppointments);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format phone number for WhatsApp (convert 05XXXXXXXX to 9725XXXXXXXX)
  const formatPhoneForWhatsApp = (phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');

    // If starts with 0, replace with 972
    if (cleanPhone.startsWith('0')) {
      return '972' + cleanPhone.substring(1);
    }

    // If already has country code, return as is
    if (cleanPhone.startsWith('972')) {
      return cleanPhone;
    }

    // Otherwise, add 972 prefix
    return '972' + cleanPhone;
  };

  // Send WhatsApp reminder
  const sendWhatsAppReminder = (appointment) => {
    const phone = formatPhoneForWhatsApp(appointment.phone);
    const time = appointment.training_time.substring(0, 5);

    // Create message in Hebrew
    const message = `שלום ${appointment.full_name},\n\nזו תזכורת לאימון שלך היום ב-${time}.\nסוג אימון: ${appointment.training_type}\n\nמחכים לך!\nPilates House 🏋️`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);

    // Open WhatsApp
    const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const groupTodayByHour = (appointments) => {
    const grouped = {};
    appointments.forEach((apt) => {
      const hour = apt.training_time.substring(0, 5);
      if (!grouped[hour]) grouped[hour] = [];
      grouped[hour].push(apt);
    });
    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map((hour) => ({ hour, appointments: grouped[hour] }));
  };

  const todayByHour = groupTodayByHour(todayAppointments);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>לוח בקרה</h1>
        <p>סקירה כללית של המערכת</p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon">👥</div>
          <div className="admin-stat-content">
            <h3>סך הכל משתמשים</h3>
            <p className="admin-stat-number">{stats.totalUsers}</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon">✅</div>
          <div className="admin-stat-content">
            <h3>מנויים פעילים</h3>
            <p className="admin-stat-number">{stats.activeUsers}</p>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-icon">📝</div>
          <div className="admin-stat-content">
            <h3>תורים קרובים</h3>
            <p className="admin-stat-number">{stats.totalAppointments}</p>
          </div>
        </div>
      </div>

      {/* Today's Appointments */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>תורים להיום</h2>
        </div>

        <div className="admin-table-container">
          {todayByHour.length > 0 ? (
            todayByHour.map(({ hour, appointments }) => (
              <details key={hour} className="admin-date-group">
                <summary className="admin-date-header" style={{ cursor: 'pointer' }}>
                  שעה {hour} ({appointments.length} נרשמים)
                </summary>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>שם מתאמן</th>
                      <th>טלפון</th>
                      <th>שעה</th>
                      <th>סוג אימון</th>
                      <th>תזכורת</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.map((apt, index) => (
                      <tr key={`${hour}-${index}`}>
                        <td>{apt.full_name}</td>
                        <td>{apt.phone}</td>
                        <td>{apt.training_time.substring(0, 5)}</td>
                        <td>
                          <span className="admin-badge">{apt.training_type}</span>
                        </td>
                        <td>
                          <button
                            className="admin-btn-whatsapp"
                            onClick={() => sendWhatsAppReminder(apt)}
                            title="שלח תזכורת WhatsApp"
                          >
                            <span style={{ fontSize: '18px' }}>💬</span> WhatsApp
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            ))
          ) : (
            <div className="admin-empty-state">
              <p>אין תורים להיום</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>פעולות מהירות</h2>
        </div>

        <div className="admin-quick-actions">
          <a href="/admin/users" className="admin-action-card">
            <div className="admin-action-icon">👤</div>
            <h3>הוסף משתמש</h3>
            <p>צור חשבון משתמש חדש</p>
          </a>

          <a href="/admin/sessions" className="admin-action-card">
            <div className="admin-action-icon">🏋️</div>
            <h3>הוסף אימון</h3>
            <p>צור אימון חדש</p>
          </a>

          <a href="/admin/notifications" className="admin-action-card">
            <div className="admin-action-icon">📢</div>
            <h3>שלח הודעה</h3>
            <p>שלח הודעה למשתמשים</p>
          </a>

          <a href="/admin/appointments" className="admin-action-card">
            <div className="admin-action-icon">📊</div>
            <h3>צפה בתורים</h3>
            <p>ראה את כל התורים</p>
          </a>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
